import { ButtonList, FlexBox } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { Message } from 'lib-shared-legacy-types';
import moment from 'moment-timezone';
import { Fragment, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { appActions } from 'app/app.actions';
import { getMessageSanitizationChannel } from 'app/utils/htmlSanitizer';
import { getMessageMarkup } from 'app/utils/messages';
import { TranslateTextChangeData } from 'components/translateTextDropdownSelectionList';
import { TranslationBadge } from 'components/translationBadge';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { Message as MessageComp, MessageProps } from '../message';

export type TextMessageProps = {
  /** The original message */
  message: Message;
  /** Internal link catch. For blocking page load and use React-Router instead */
  catchInternalLinks?: boolean;
  /**
   * The translation object containing all the necessary message translation
   * information.
   */
  translation?: TranslateTextChangeData;
  isNotRedactable?: boolean;
} & MessageProps;

const TextMessage = ({
  message,
  catchInternalLinks,
  translation,
  nameMenu,
  menu,
  isNotRedactable,
  ...otherProps
}: TextMessageProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const hasRedactAccess = useHasAccess('conversation.redact');
  const { redactMessages } = useFeatureFlag(['redact-messages']);

  const sanitizationChannel = getMessageSanitizationChannel(message);

  const handleCatchInternalLinks = (e: React.MouseEvent) => {
    if (e.target instanceof HTMLAnchorElement) {
      const origin = window.location.origin;
      const href = e.target.href;
      if (href.indexOf(origin) === 0) {
        e.preventDefault();
        const relativeUrl = href.replace(origin, '');
        navigate(relativeUrl);
      }
    }
  };

  const preparedMarkup = useMemo(() => {
    if (translation?.status === 'fulfilled' && translation) {
      return getMessageMarkup({ ...message, text: translation.translatedText });
    }

    return getMessageMarkup(message);
  }, [message, translation]);

  const nameMenuContent = useMemo(() => {
    return (
      <FlexBox gap="200" alignItems="center">
        {nameMenu}

        {translation?.status && (
          <TranslationBadge
            translation={translation}
            originalLanguageCode={message.language ?? undefined}
            errorMessage="There was an error translating this message. Please try again."
          />
        )}
      </FlexBox>
    );
  }, [message.language, nameMenu, translation]);

  const markup = useMemo(() => {
    if (message.anonymized_at && preparedMarkup.length == 0) {
      const utcMomentTime = moment(message.anonymized_at).utc().local();
      return `Message anonymized at ${utcMomentTime.format('MMM D, YYYY · HH:mm:ss')}`;
    }
    return preparedMarkup;
  }, [preparedMarkup, message.anonymized_at]);

  const menuWithRedactOption = useMemo(() => {
    const csid = otherProps.csid;
    const messageId = message.id;
    const isRedactable =
      !otherProps.isAnonymized &&
      message.direction === 'inbound' &&
      hasRedactAccess &&
      redactMessages &&
      !isNotRedactable;
    const hasContent =
      (preparedMarkup && preparedMarkup.length > 0) ||
      (message.text && message.text.length > 0);
    const hasImages = message.inline_images && message.inline_images.length > 0;
    const hasAttachments =
      message.attached_files &&
      (!Array.isArray(message.attached_files) ||
        message.attached_files.length > 0);
    if (
      csid &&
      messageId &&
      isRedactable &&
      (hasContent || hasImages || hasAttachments)
    ) {
      const redactOption = (
        <Fragment key="redact">
          {menu && menu.length > 0 && <ButtonList.Divider />}
          <ButtonList.Item
            icon="transcript"
            onClick={() => {
              dispatch(
                appActions.showModal({
                  modal: {
                    type: 'conversation:redactMessage',
                    arg: {
                      csid,
                      messageId,
                      content: message.form_id
                        ? (message.text ?? preparedMarkup)
                        : preparedMarkup,
                      images: message.inline_images,
                      attachments: message.attached_files,
                    },
                  },
                }),
              );
            }}
          >
            Redact
          </ButtonList.Item>
        </Fragment>
      );
      return [...(menu ?? []), redactOption];
    }
    return menu;
  }, [
    dispatch,
    hasRedactAccess,
    menu,
    message.attached_files,
    message.direction,
    message.form_id,
    message.id,
    message.inline_images,
    message.text,
    otherProps.csid,
    otherProps.isAnonymized,
    preparedMarkup,
    redactMessages,
  ]);

  return (
    <MessageComp
      {...otherProps}
      onClick={catchInternalLinks ? handleCatchInternalLinks : undefined}
      markup={markup}
      nameMenu={nameMenuContent}
      channel={sanitizationChannel}
      menu={menuWithRedactOption}
    />
  );
};

export default TextMessage;
